import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { settings } from "@microsoft/teams-js";
import { Dropdown, Loader, Flex, Text, Button } from '@fluentui/react-northstar';
import HopexApi from '../api/HopexApi';
import { IDiagram } from '../models/IDiagram';
import AppContext from '../contexts/AppContext';
import Login from '../components/login/Login';
import ErrorAlert from '../components/errorAlert/ErrorAlert';
import SignoutIcon from '../components/icons/SignoutIcon';
import { IHopexCredentials } from '../models/IHopexCredentials';

const ConfigurationRoute = () => {
    const { t } = useTranslation();
    const { teamsContext, hopexContext, setHopexContext } = useContext(AppContext);
    const [diagrams, setDiagrams] = useState<IDiagram[]>();
    const [diagramPicture, setDiagramPicture] = useState<string>();
    const [selectedDiagram, setSelectedDiagram] = useState<IDiagram>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [tabConfigCredentials, setTabConfigCredentials] = useState<IHopexCredentials>();

    let canUseStorage = true;
    // Check is localstorage is available or not
    try {
        localStorage.getItem('test');
    } catch {
        canUseStorage = false;
    }

    useEffect(() => {
        if (teamsContext) {
            settings.getSettings((currentSettings) => {
                console.log(currentSettings);
                if (currentSettings.contentUrl) {
                    const configParams = new URLSearchParams(currentSettings.contentUrl);

                    const urlCredentials = configParams.get('credentials');
                    if (urlCredentials) {
                        try {
                            const confCreds: IHopexCredentials = JSON.parse(atob(urlCredentials));
                            setTabConfigCredentials(confCreds);
                        }
                        catch {
                            // Issue when trying to parse json, don't reuse the configuration
                        }
                    }
                }
            });
        }
    }, [teamsContext]);


    // handler when click on Save button
    // Save tab with following parameters in URL: diagram id (also the entityId) + tab credentials (read only)
    const handleSaveClick = async (event: settings.SaveEvent) => {
        if (selectedDiagram && hopexContext) {
            settings.setSettings({
                contentUrl: `${window.location.origin}/tabs/diagram/?diagid=${encodeURIComponent(selectedDiagram.id)}&credentials=${btoa(JSON.stringify(hopexContext.credentials))}`,
                //websiteUrl: generateDiagramDirectLink(),
                entityId: selectedDiagram.id || 'diagram',
                suggestedDisplayName: selectedDiagram.name.length > 100 ? selectedDiagram.name.substr(0, 100) : selectedDiagram.name,
            });
            event.notifySuccess();
        }
        else {
            event.notifyFailure();
        }
    };

    // Register tab config save handler
    settings.registerOnSaveHandler(handleSaveClick);

    useEffect(() => {
        if (hopexContext && !diagrams) {
            // reset potential previous error message
            setErrorMessage(undefined);
            // get all diagram for current credentials
            HopexApi.getDiagrams(hopexContext.credentials)
                .then((diagramsResp) => {
                    // make them available in the search box
                    setDiagrams(diagramsResp.data.data.diagram || []);


                    //const urlDiagId = urlparams.get('diagid');

                }).catch((reason) => {
                    setErrorMessage('Error.CannotGetDiagrams');
                });
        }
    }, [hopexContext, diagrams]);

    useEffect(() => {
        if (teamsContext) {
            settings.getSettings((currentSettings) => {
                if (currentSettings.contentUrl) {

                }
            });
        }
    }, [teamsContext]);

    useEffect(() => {
        if (selectedDiagram && hopexContext) {
            // If a diagram is selected and Hopex context is OK, user can save the tab 
            settings.setValidityState(true);
            // use SVG (got issues with other formats)
            const imageType = 'image/svg+xml';
            HopexApi.getDiagramPicture(
                hopexContext.credentials,
                selectedDiagram.downloadUrl,
                imageType,
            ).then((response) => {
                setDiagramPicture(response.data);
            }).catch(() => {
                setDiagramPicture(undefined);
            });
        } else {
            // Else disable save button
            settings.setValidityState(false);
            setDiagramPicture(undefined);
        }
    }, [selectedDiagram, hopexContext]);

    const handleLogout = () => {
        // Logout remove local cached credentials
        if (canUseStorage) {
            localStorage.removeItem('serverUrl');
        }
        // reset state variables
        setHopexContext(undefined);
        setDiagrams(undefined);
        setSelectedDiagram(undefined);
        setDiagramPicture(undefined);
        setErrorMessage(undefined);
    };

    return (<Flex column gap={"gap.medium"}>
        {false && <Text content={t("Configure.Welcome")} as="h2" styles={{ marginBlockStart: 0 }} />}
        {!hopexContext && (
            <Login isInPopin={false} defaultCredentials={tabConfigCredentials} />
        )}
        {hopexContext && diagrams && (<>
            <Flex hAlign="stretch" gap={"gap.medium"} fill space={"between"}>
                <div>
                    <Text content={t('Common.ServerUrl')} size="small" /> :
                    <Text content={hopexContext.credentials.serverUrl} size="small" />
                </div>
                <Button
                    text
                    content={t('Common.Logout')}
                    icon={<SignoutIcon color={'#30ac6b'} />}
                    iconPosition='before'
                    size="small"
                    onClick={handleLogout} />
            </Flex>
            <Text content={t("Configure.SelectDiagramSection")} as="h3" />
            <Text content={t('Configure.Diagram')} />
            <Dropdown
                search
                fluid
                clearable
                onChange={(ev, data) => {
                    if (data && data.value && diagrams) {
                        const diagram = diagrams.find((d) => d.id === (data?.value as { key: string, header: string }).key);
                        setSelectedDiagram(diagram);
                    }
                    else {
                        setSelectedDiagram(undefined);
                    }
                }}
                items={(diagrams || []).map((diag) => ({ key: diag.id, header: diag.name }))}
                placeholder={t('Configure.SelectDiagramPlaceholder')}
                noResultsMessage={t('Configure.SelectDiagramNoResultsMessage')}
                a11ySelectedItemsMessage={t('Configure.SelectDiagramA11ySelectedItemsMessage')}
            />
            {selectedDiagram && !diagramPicture && (
                <Loader size="medium" />
            )}
            {selectedDiagram && diagramPicture && (
                <div className="diagram-picture-container" dangerouslySetInnerHTML={{ __html: diagramPicture }}></div>
            )}
        </>)}
        {hopexContext && !diagrams && (
            <Loader size="medium" label={t("Configure.LoadingDiagrams")} />
        )}
        {errorMessage && (<ErrorAlert message={errorMessage} compact={true} />)}
    </Flex>)
}

export default ConfigurationRoute;
